import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate, Navigate, useSearchParams, useLocation, Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import {
  LayoutDashboard, CreditCard, Send, Stamp, Gavel, FileCode2, ShieldAlert, Trophy, LogOut, MapPin, Scale, Store, Zap, Sparkles, Percent,
} from "lucide-react";
import { Atms, Compliance, StampTransferInline, ClaimStampBox } from "./Extras";
import CardTracker from "./CardTracker";
import { TopUp } from "./Stripe";
import { Plans, Shares } from "./Plans";

function TopTicker() {
  const [rates, setRates] = useState([]);
  useEffect(() => {
    const load = () => api.get("/exchange-rates").then((r) => setRates(Object.values(r.data.rates || {}))).catch(()=>{});
    load(); const t = setInterval(load, 5000); return () => clearInterval(t);
  }, []);
  return (
    <div className="border-b border-white/10 bg-black/60" data-testid="dashboard-ticker">
      <Marquee gradient={false} speed={35} pauseOnHover>
        {rates.map((r) => (
          <div key={r.code} className="ticker-item">
            <span className="pulse-dot"/> <b>{r.code}</b> <span className="text-white/50">{r.name}</span>
            <span className="text-[#d4af37]">${r.rate_usd?.toFixed(4)}</span>
            <span className={r.change_pct >= 0 ? "text-[#39ff14]" : "text-[#ff3366]"}>{r.change_pct >= 0 ? "▲" : "▼"} {Math.abs(r.change_pct).toFixed(3)}%</span>
          </div>
        ))}
      </Marquee>
    </div>
  );
}

const NAV_TREE = [
  { group: "Wallet",     items: [
    { to: "cards",       label: "Virtual Cards" },
    { to: "send",        label: "Send Money" },
    { to: "stamps",      label: "Tax Stamps" },
    { to: "topup",       label: "Top Up (Stripe)" },
  ]},
  { group: "Membership", items: [
    { to: "plans",       label: "Plans" },
    { to: "shares",      label: "Shares & Dividends" },
  ]},
  { group: "Payments",   items: [
    { to: "merchant",    label: "Merchant Checkout" },
    { to: "chargebacks", label: "Chargebacks" },
  ]},
  { group: "Network",    items: [
    { to: "atms",        label: "ATM Network" },
    { to: "sweeps",      label: "Sweepstakes" },
  ]},
  { group: "Compliance", items: [
    { to: "iso",         label: "ISO 20022" },
    { to: "compliance",  label: "Compliance" },
    { to: "audit",       label: "Audit Trail" },
  ]},
];

function findNavItem(slug) {
  for (const g of NAV_TREE) for (const it of g.items) if (it.to === slug) return { group: g.group, label: it.label };
  return null;
}

function Breadcrumbs() {
  const loc = useLocation();
  const nav = useNavigate();
  const slug = loc.pathname.replace(/^\/app\/?/, "").split("/")[0];
  const meta = slug ? findNavItem(slug) : null;
  const allItems = NAV_TREE.flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })));
  return (
    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap" data-testid="breadcrumbs">
      <div className="flex items-center gap-2 text-sm text-white/60 font-mono">
        <Link to="/app" className="hover:text-white" data-testid="bc-home">Overview</Link>
        {meta && (
          <>
            <span className="text-white/30">›</span>
            <span className="text-white/50">{meta.group}</span>
            <span className="text-white/30">›</span>
            <span className="text-[#d4af37]" data-testid="bc-current">{meta.label}</span>
          </>
        )}
      </div>
      <select
        data-testid="quick-jump"
        value={slug || ""}
        onChange={(e) => nav(e.target.value ? e.target.value : "")}
        className="input py-1.5 text-xs max-w-xs"
        style={{width:"auto", minWidth:"220px"}}
      >
        <option value="">Overview</option>
        {NAV_TREE.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.items.map((it) => <option key={it.to} value={it.to}>{it.label}</option>)}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const iconFor = { cards: CreditCard, send: Send, stamps: Stamp, topup: Zap, plans: Sparkles, shares: Percent, merchant: Store, chargebacks: Gavel, atms: MapPin, sweeps: Trophy, iso: FileCode2, compliance: Scale, audit: ShieldAlert };
  const item = "flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors";
  const active = "bg-white/10 text-white border border-white/10";
  const groupCls = "text-[10px] tracking-[0.25em] text-white/40 uppercase font-semibold px-4 mt-5 mb-2";
  return (
    <aside className="w-64 min-h-screen border-r border-white/10 p-5 hidden lg:block">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#7a5f16] grid place-items-center font-display font-black text-black">★</div>
        <div>
          <div className="font-display font-bold leading-none">BlackStar</div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-white/50">VIRTUAL MINT</div>
        </div>
      </div>
      <nav className="space-y-0.5">
        <NavLink to="" end data-testid="nav-overview" className={({isActive}) => `${item} ${isActive ? active : ""}`}><LayoutDashboard size={16}/> Overview</NavLink>
        {NAV_TREE.map((g) => (
          <div key={g.group}>
            <div className={groupCls}>{g.group}</div>
            {g.items.map((it) => {
              const Icon = iconFor[it.to] || LayoutDashboard;
              return (
                <NavLink key={it.to} to={it.to} data-testid={`nav-${it.to}`} className={({isActive}) => `${item} ${isActive ? active : ""}`}>
                  <Icon size={16}/> {it.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="mt-6 panel p-4">
        <div className="label mb-2">Signed in</div>
        <div className="text-sm truncate" data-testid="sidebar-user-email">{user?.email}</div>
        <button data-testid="logout-btn" onClick={async ()=>{ await logout(); nav("/"); }} className="mt-3 btn-ghost w-full text-xs inline-flex items-center justify-center gap-2"><LogOut size={12}/> Sign out</button>
      </div>
    </aside>
  );
}

function Overview() {
  const [accts, setAccts] = useState([]);
  const [txns, setTxns] = useState([]);
  useEffect(() => {
    api.get("/accounts").then((r)=>setAccts(r.data)).catch(()=>{});
    api.get("/transactions").then((r)=>setTxns(r.data)).catch(()=>{});
  }, []);
  const total = accts.reduce((s,a)=>s+(a.balance||0), 0);
  return (
    <div className="space-y-8" data-testid="overview-panel">
      <div>
        <div className="label text-[#d4af37]">Vault Total (Credit-Initiated)</div>
        <div className="font-display text-6xl mt-2 text-[#39ff14]" data-testid="total-balance">${total.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
        <div className="text-white/50 text-sm mt-1 font-mono">Seeded by database at state S06 · redeemable end-to-end</div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {accts.map((a) => (
          <div key={a.id} className="panel p-6" data-testid={`account-card-${a.tier}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="chip gold">{a.tier?.toUpperCase()}</span>
              <span className="chip">{a.type}</span>
            </div>
            <div className="font-mono text-xs text-white/50">A/C {a.account_number}</div>
            <div className="font-display text-3xl mt-3">${(a.balance||0).toLocaleString(undefined,{minimumFractionDigits:2})}</div>
            <div className="mt-4 space-y-1 text-xs text-white/50 font-mono">
              <div>IBAN {a.iban}</div>
              <div>BIC {a.bic}</div>
              <div>SORT {a.sort_code}</div>
            </div>
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-display text-2xl mb-4">Recent Transactions</h3>
        <div className="panel">
          {txns.length === 0 && <div className="p-6 text-white/50 text-sm">No transactions yet. Try Send Money.</div>}
          {txns.slice(0,10).map((t) => (
            <div key={t.id} className="flex items-center justify-between px-6 py-4 border-b border-white/5" data-testid={`txn-row-${t.reference}`}>
              <div>
                <div className="text-sm">{t.recipient_name}</div>
                <div className="font-mono text-[11px] text-white/40">{t.reference} · {t.method}</div>
              </div>
              <div className="text-right">
                <div className="font-mono">-${t.amount?.toFixed(2)} <span className="text-white/50">{t.currency}</span></div>
                <div className="text-[10px] text-[#39ff14] font-mono">SETTLED · S13</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Cards() {
  const [cards, setCards] = useState([]);
  const [reveal, setReveal] = useState(null);
  const [dispatchFor, setDispatchFor] = useState(null);
  const [addr, setAddr] = useState({ address_line1:"", city:"", country:"GB", postcode:"" });
  const [dispatched, setDispatched] = useState(null);
  const load = () => api.get("/cards").then((r)=>setCards(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const issue = async () => {
    try { const { data } = await api.post("/cards/issue"); setReveal(data); toast.success("Card provisioned"); load(); }
    catch(e){ toast.error(errText(e)); }
  };
  const act = async (id, path) => {
    try { await api.post(`/cards/${id}/${path}`); load(); toast.success(path); } catch(e){ toast.error(errText(e)); }
  };
  const load$ = async (id) => {
    try { await api.post(`/cards/${id}/load`, { amount: 100 }); load(); toast.success("+$100 loaded"); } catch(e){ toast.error(errText(e)); }
  };
  const submitDispatch = async (e) => {
    e.preventDefault();
    if (!addr.address_line1 || !addr.city || !addr.postcode) return toast.error("Complete the address");
    try {
      const { data } = await api.post(`/cards/${dispatchFor}/dispatch-physical`, addr);
      setDispatched(data); setDispatchFor(null);
      toast.success(`RFID dispatch queued · ${data.tracking_number}`);
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-6" data-testid="cards-panel">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">Virtual Cards</h1>
        <button data-testid="issue-card-btn" onClick={issue} className="btn-primary">+ Issue new card</button>
      </div>
      {reveal && (
        <div className="panel p-6 border-[#d4af37]/40" data-testid="card-reveal">
          <div className="label mb-2">One-time CVV reveal</div>
          <div className="font-mono text-2xl text-[#d4af37]">{reveal.card_number_masked} · CVV {reveal.cvv_reveal_once}</div>
          <button onClick={()=>setReveal(null)} className="btn-ghost mt-4 text-xs">Dismiss</button>
        </div>
      )}
      {dispatched && (
        <div className="panel p-6 border-[#39ff14]/30" data-testid="dispatch-result">
          <div className="chip green mb-2">THERMAL RFID QUEUED</div>
          <div className="font-mono text-sm text-white/80">Tracking {dispatched.tracking_number}</div>
          <div className="text-xs text-white/60 mt-1">{dispatched.technology} · {dispatched.printing_facility} · ETA {new Date(dispatched.eta).toLocaleDateString()}</div>
          <button onClick={()=>setDispatched(null)} className="btn-ghost mt-3 text-xs">Dismiss</button>
        </div>
      )}
      {dispatchFor && (
        <form onSubmit={submitDispatch} className="panel p-6 space-y-3" data-testid="dispatch-form">
          <div className="label">Ship Thermal RFID card</div>
          <input data-testid="ship-line1" className="input" placeholder="Street" value={addr.address_line1} onChange={(e)=>setAddr({...addr, address_line1:e.target.value})}/>
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="ship-city" className="input" placeholder="City" value={addr.city} onChange={(e)=>setAddr({...addr, city:e.target.value})}/>
            <input data-testid="ship-country" className="input" placeholder="Country" value={addr.country} onChange={(e)=>setAddr({...addr, country:e.target.value})}/>
            <input data-testid="ship-postcode" className="input" placeholder="Postcode" value={addr.postcode} onChange={(e)=>setAddr({...addr, postcode:e.target.value})}/>
          </div>
          <div className="flex gap-3">
            <button data-testid="ship-submit" className="btn-primary">Queue thermal print</button>
            <button type="button" onClick={()=>setDispatchFor(null)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        {cards.map((c) => (
          <div key={c.id} data-testid={`card-wrap-${c.id}`}>
            <div className="card-3d p-6 aspect-[1.6/1] flex flex-col justify-between" data-testid={`card-${c.id}`}>
              <div className="flex justify-between items-start relative z-10">
                <div className="font-display text-2xl">★</div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#d4af37]">{c.brand}</div>
              </div>
              <div className="relative z-10">
                <div className="font-mono text-lg tracking-widest">{c.card_number_masked}</div>
                <div className="flex justify-between mt-3 text-xs text-white/70">
                  <span>{c.card_holder_name}</span>
                  <span>{String(c.expiry_month).padStart(2,"0")}/{String(c.expiry_year).slice(-2)}</span>
                  <span className={c.is_frozen ? "text-[#ff3366]" : "text-[#39ff14]"}>{c.is_frozen ? "FROZEN" : "ACTIVE"}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 relative z-10">
                  {c.is_frozen
                    ? <button data-testid={`unfreeze-${c.id}`} onClick={()=>act(c.id,"unfreeze")} className="btn-ghost text-xs">Unfreeze</button>
                    : <button data-testid={`freeze-${c.id}`} onClick={()=>act(c.id,"freeze")} className="btn-ghost text-xs">Freeze</button>}
                  <button data-testid={`load-${c.id}`} onClick={()=>load$(c.id)} className="btn-ghost text-xs">Load $100</button>
                  <button data-testid={`ship-${c.id}`} onClick={()=>setDispatchFor(c.id)} className="btn-ghost text-xs">Ship RFID</button>
                  <div className="ml-auto font-mono text-xs text-white/80">Bal ${c.balance?.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <CardTracker cardId={c.id}/>
          </div>
        ))}
        {cards.length === 0 && <div className="panel p-8 text-white/60">No cards yet. Issue your first BlackStar card.</div>}
      </div>
    </div>
  );
}

function StateTrace({ trace }) {
  return (
    <div className="mt-6">
      <div className="label mb-3">State-Machine Trace S00 → S13</div>
      <div>
        {trace.map((s) => (
          <div key={s.state} className="state-node" data-testid={`trace-${s.state}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[#d4af37]">{s.state}</span>
              <span className="text-sm">{s.name}</span>
              <span className="ml-auto font-mono text-[10px] text-white/40">{new Date(s.at).toISOString().slice(11,23)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SendMoney() {
  const [sp] = useSearchParams();
  const preAtm = sp.get("atm");
  const preCountry = sp.get("country");
  const currencyByCountry = { GB:"GBP", US:"USD", JP:"JPY", DE:"EUR", FR:"EUR", CH:"CHF", CA:"CAD", CN:"CNY", HK:"HKD", SG:"SGD", AU:"AUD", AE:"AED", IN:"INR", BR:"BRL", ZA:"ZAR", NG:"NGN", KR:"KRW" };
  const [form, setForm] = useState({
    method:"atm_money_order",
    amount: 250,
    currency: (preCountry && currencyByCountry[preCountry]) || "USD",
    recipient_name: preAtm ? `ATM ${preAtm}` : "Jane Doe",
    recipient_sort_code:"20-00-00",
    recipient_iban:"GB29NWBK60161331926819",
    recipient_bic:"NWBKGB2LXXX",
    description: preAtm ? `Cash pickup at ${preAtm}` : "Rent",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/send-money", { ...form, amount: Number(form.amount) });
      setResult(data); toast.success("Money order dispatched · S13 settled");
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };
  const set = (k,v) => setForm((f)=>({...f, [k]:v }));
  return (
    <div className="space-y-6" data-testid="send-panel">
      <h1 className="font-display text-3xl">Send Money · ATM IP-Mapped Money Order</h1>
      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="panel p-6 space-y-4" data-testid="send-form">
          <div>
            <span className="label">Method</span>
            <select data-testid="send-method" className="input mt-2" value={form.method} onChange={(e)=>set("method", e.target.value)}>
              <option value="atm_money_order">ATM Cash-Redeemable Money Order</option>
              <option value="bank_transfer">Bank Transfer (FPS/Fedwire)</option>
              <option value="card">Card Rail</option>
              <option value="gateway">Payment Gateway</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">Amount</span><input data-testid="send-amount" className="input mt-2" type="number" value={form.amount} onChange={(e)=>set("amount", e.target.value)}/></div>
            <div><span className="label">Currency</span>
              <select data-testid="send-currency" className="input mt-2" value={form.currency} onChange={(e)=>set("currency", e.target.value)}>
                {["USD","EUR","GBP","JPY","CNY","CHF","CAD","STC"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><span className="label">Recipient name</span><input data-testid="send-recipient" className="input mt-2" value={form.recipient_name} onChange={(e)=>set("recipient_name", e.target.value)}/></div>
          <div><span className="label">Sort code</span><input data-testid="send-sortcode" className="input mt-2" value={form.recipient_sort_code} onChange={(e)=>set("recipient_sort_code", e.target.value)}/></div>
          <div><span className="label">IBAN</span><input data-testid="send-iban" className="input mt-2" value={form.recipient_iban} onChange={(e)=>set("recipient_iban", e.target.value)}/></div>
          <div><span className="label">BIC</span><input data-testid="send-bic" className="input mt-2" value={form.recipient_bic} onChange={(e)=>set("recipient_bic", e.target.value)}/></div>
          <button data-testid="send-submit" className="btn-primary w-full" disabled={loading}>{loading ? "Routing…" : "Dispatch Money Order"}</button>
        </form>
        <div>
          {result ? (
            <div className="panel p-6" data-testid="send-result">
              <div className="chip green">SETTLED · REF {result.reference}</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm font-mono">
                <div>IP <span className="text-white/60">{result.ip_mapping.source_ip}</span></div>
                <div>Country <span className="text-white/60">{result.ip_mapping.resolved_country}</span></div>
                <div>BIC <span className="text-white/60">{result.ip_mapping.resolved_bic}</span></div>
                <div>Sort <span className="text-white/60">{result.ip_mapping.resolved_sort_code}</span></div>
                <div>Bank <span className="text-white/60">{result.ip_mapping.central_bank}</span></div>
                <div>Rail <span className="text-white/60">{result.ip_mapping.network}</span></div>
              </div>
              {result.atm_redeemable && <div className="mt-4 chip cyan">✓ Cash-redeemable at any BlackStar ATM</div>}
              <StateTrace trace={result.state_trace} />
            </div>
          ) : (
            <div className="panel p-6 text-white/50 text-sm">Result will appear here — IP-banking mapping and the full S00→S13 trace.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stamps() {
  const [stamps, setStamps] = useState([]);
  const load = () => api.get("/tax-stamps").then((r)=>setStamps(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const issue = async () => { try { await api.post("/tax-stamps/issue"); toast.success("Stamp issued"); load(); } catch(e){ toast.error(errText(e)); } };
  const redeem = async (id) => { try { const { data } = await api.post("/tax-stamps/redeem", { stamp_id: id }); toast.success(`Credited ${data.credited} ${data.currency}`); load(); } catch(e){ toast.error(errText(e)); } };
  return (
    <div className="space-y-6" data-testid="stamps-panel">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">Digital Tax-Credit Stamps</h1>
        <button data-testid="issue-stamp-btn" onClick={issue} className="btn-primary">+ Issue stamp</button>
      </div>
      <ClaimStampBox onDone={load}/>
      <div className="grid md:grid-cols-3 gap-4">
        {stamps.map((s) => (
          <div key={s.id} className="panel p-6" data-testid={`stamp-${s.id}`}>
            <div className="chip gold mb-3">{s.series}</div>
            <div className="font-display text-4xl">{s.currency === "GBP" ? "£" : s.currency === "EUR" ? "€" : "$"}{s.denomination}</div>
            <div className={`mt-3 text-xs font-mono ${s.status === "redeemed" ? "text-white/40" : s.status === "in_transit" ? "text-[#00f0ff]" : "text-[#39ff14]"}`}>{s.status.toUpperCase()}</div>
            {s.status === "issued" && <button data-testid={`redeem-${s.id}`} onClick={()=>redeem(s.id)} className="btn-ghost mt-4 text-xs w-full">Redeem to Vault</button>}
            {s.status === "issued" && <StampTransferInline stamp={s} onDone={load}/>}
          </div>
        ))}
        {stamps.length === 0 && <div className="panel p-8 text-white/60 col-span-full">No stamps yet.</div>}
      </div>
    </div>
  );
}

function Chargebacks() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ transaction_ref:"MO-XXXXXXXX", amount: 100, currency:"USD", reason:"unauthorised", description:"Card not present" });
  const load = () => api.get("/chargebacks").then((r)=>setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/chargebacks", { ...form, amount: Number(form.amount) }); toast.success("Chargeback filed"); load(); } catch(e){ toast.error(errText(e)); } };
  return (
    <div className="space-y-6" data-testid="chargebacks-panel">
      <h1 className="font-display text-3xl">Chargeback / Dispute Center</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="panel p-6 space-y-3" data-testid="chargeback-form">
          <div><span className="label">Txn reference</span><input data-testid="cb-ref" className="input mt-2" value={form.transaction_ref} onChange={(e)=>setForm({...form, transaction_ref:e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">Amount</span><input data-testid="cb-amount" className="input mt-2" type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})}/></div>
            <div><span className="label">Reason</span>
              <select data-testid="cb-reason" className="input mt-2" value={form.reason} onChange={(e)=>setForm({...form, reason:e.target.value})}>
                {["unauthorised","fraud","not_received","duplicate","service_dispute"].map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div><span className="label">Details</span><textarea data-testid="cb-details" className="input mt-2" rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})}/></div>
          <button data-testid="cb-submit" className="btn-primary w-full">File dispute</button>
        </form>
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="panel p-5" data-testid={`cb-${c.id}`}>
              <div className="flex justify-between">
                <div className="font-mono text-xs text-white/50">{c.transaction_ref}</div>
                <span className="chip cyan">{c.status.replaceAll("_"," ").toUpperCase()}</span>
              </div>
              <div className="mt-2 font-display text-xl">${c.amount} {c.currency}</div>
              <div className="text-sm text-white/60 mt-1">{c.reason} · {c.description}</div>
            </div>
          ))}
          {items.length === 0 && <div className="panel p-6 text-white/50 text-sm">No disputes filed.</div>}
        </div>
      </div>
    </div>
  );
}

function ISO() {
  const [form, setForm] = useState({ message_type:"pacs.008", debtor_name:"M. Agada", debtor_iban:"GB29BSVM60161331926819", creditor_name:"Jane Doe", creditor_iban:"GB33NWBK60161331926820", amount:1500, currency:"USD", remittance_info:"Invoice 4421" });
  const [xml, setXml] = useState("");
  const gen = async () => { try { const { data } = await api.post("/iso20022/generate", { ...form, amount: Number(form.amount) }); setXml(data.xml); toast.success("Message generated"); } catch(e){ toast.error(errText(e)); } };
  return (
    <div className="space-y-6" data-testid="iso-panel">
      <h1 className="font-display text-3xl">ISO 20022 Message Composer</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel p-6 space-y-3">
          <div><span className="label">Message type</span>
            <select data-testid="iso-type" className="input mt-2" value={form.message_type} onChange={(e)=>setForm({...form, message_type:e.target.value})}>
              <option>pacs.008</option><option>pacs.002</option><option>camt.053</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">Debtor</span><input data-testid="iso-debtor" className="input mt-2" value={form.debtor_name} onChange={(e)=>setForm({...form, debtor_name:e.target.value})}/></div>
            <div><span className="label">Debtor IBAN</span><input data-testid="iso-debtor-iban" className="input mt-2" value={form.debtor_iban} onChange={(e)=>setForm({...form, debtor_iban:e.target.value})}/></div>
            <div><span className="label">Creditor</span><input data-testid="iso-creditor" className="input mt-2" value={form.creditor_name} onChange={(e)=>setForm({...form, creditor_name:e.target.value})}/></div>
            <div><span className="label">Creditor IBAN</span><input data-testid="iso-creditor-iban" className="input mt-2" value={form.creditor_iban} onChange={(e)=>setForm({...form, creditor_iban:e.target.value})}/></div>
            <div><span className="label">Amount</span><input data-testid="iso-amount" className="input mt-2" type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})}/></div>
            <div><span className="label">Currency</span><input data-testid="iso-currency" className="input mt-2" value={form.currency} onChange={(e)=>setForm({...form, currency:e.target.value})}/></div>
          </div>
          <div><span className="label">Remittance</span><input data-testid="iso-rem" className="input mt-2" value={form.remittance_info} onChange={(e)=>setForm({...form, remittance_info:e.target.value})}/></div>
          <button data-testid="iso-generate-btn" onClick={gen} className="btn-primary w-full">Generate XML</button>
        </div>
        <div className="panel-solid p-4 overflow-auto max-h-[520px]">
          <pre className="font-mono text-xs text-[#00f0ff] whitespace-pre-wrap" data-testid="iso-xml">{xml || "// XML output will appear here"}</pre>
        </div>
      </div>
    </div>
  );
}

function Merchant() {
  const [form, setForm] = useState({ merchant_name:"Vinyl Cathedral", amount: 49.99, currency:"USD", description:"Vinyl LP · Blackstar Sessions Vol.1", return_url:"", webhook_url:"" });
  const [session, setSession] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const loadDeliveries = async (sid) => {
    try { const { data } = await api.get(`/merchant/checkout/${sid}/deliveries`); setDeliveries(data.deliveries); } catch {}
  };
  useEffect(() => {
    if (!session) return;
    loadDeliveries(session.id);
    const t = setInterval(() => loadDeliveries(session.id), 15000);
    return () => clearInterval(t);
  }, [session]);
  const create = async () => {
    try { const { data } = await api.post("/merchant/checkout", { ...form, amount: Number(form.amount) }); setSession(data); setDeliveries([]); toast.success("Checkout session created"); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-6" data-testid="merchant-panel">
      <h1 className="font-display text-3xl">Pay-with-BlackStar · Merchant Checkout</h1>
      <p className="text-white/60 text-sm">Create a hosted checkout session; share the URL with any customer and they pay from their vault balance. Every payment is settlement-final at S13.</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel p-6 space-y-3">
          <div><span className="label">Merchant name</span><input data-testid="mch-name" className="input mt-2" value={form.merchant_name} onChange={(e)=>setForm({...form, merchant_name:e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">Amount</span><input data-testid="mch-amount" className="input mt-2" type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})}/></div>
            <div><span className="label">Currency</span>
              <select data-testid="mch-currency" className="input mt-2" value={form.currency} onChange={(e)=>setForm({...form, currency:e.target.value})}>
                {["USD","EUR","GBP","JPY","CNY","CHF","CAD","STC"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><span className="label">Description</span><input data-testid="mch-desc" className="input mt-2" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})}/></div>
          <div><span className="label">Return URL (optional)</span><input data-testid="mch-return" className="input mt-2" placeholder="https://your-shop.com/thanks" value={form.return_url} onChange={(e)=>setForm({...form, return_url:e.target.value})}/></div>
          <div><span className="label">Webhook URL (optional)</span><input data-testid="mch-webhook" className="input mt-2" placeholder="https://your-shop.com/hooks/blackstar" value={form.webhook_url} onChange={(e)=>setForm({...form, webhook_url:e.target.value})}/></div>
          <button data-testid="mch-create" onClick={create} className="btn-primary w-full">Create checkout session</button>
        </div>
        <div>
          {session ? (
            <div className="panel p-6" data-testid="mch-session">
              <div className="chip gold mb-2">SESSION READY</div>
              <div className="font-display text-2xl mt-2">{session.currency} {Number(session.amount).toFixed(2)}</div>
              <div className="font-mono text-xs text-white/50 mt-3">{session.id}</div>
              <div className="mt-4">
                <div className="label mb-2">Shareable checkout link</div>
                <div className="font-mono text-xs text-[#00f0ff] break-all p-3 bg-black/40 border border-white/10 rounded" data-testid="mch-link">
                  {origin}/pay/{session.id}
                </div>
                <a href={`/pay/${session.id}`} target="_blank" rel="noreferrer" data-testid="mch-open" className="btn-ghost mt-3 inline-block text-xs">Open checkout ↗</a>
              </div>
              {deliveries.length > 0 && (
                <div className="mt-5">
                  <div className="label mb-2">Webhook deliveries</div>
                  {deliveries.map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-xs py-2 border-b border-white/5" data-testid={`whd-${d.id}`}>
                      <span className={`chip ${d.delivered ? "green" : "gold"}`}>{d.delivered ? "DELIVERED" : `RETRY #${d.attempts}`}</span>
                      <span className="font-mono text-white/50">HTTP {d.status_code}</span>
                      <span className="font-mono text-white/40 text-[10px]">{d.next_retry_at ? `next ${new Date(d.next_retry_at).toLocaleTimeString()}` : "final"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="panel p-6 text-white/50 text-sm">Session preview will appear here.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/audit-logs").then((r)=>setLogs(r.data)).catch(()=>{}); }, []);
  return (
    <div data-testid="audit-panel">
      <h1 className="font-display text-3xl mb-6">Audit Trail</h1>
      <div className="panel">
        {logs.length === 0 && <div className="p-6 text-white/50 text-sm">No audit events yet.</div>}
        {logs.map((l) => (
          <div key={l.id} className="px-6 py-3 border-b border-white/5 flex justify-between font-mono text-xs">
            <span className="text-[#d4af37]">{l.action}</span>
            <span className="text-white/70">{l.details}</span>
            <span className="text-white/40">{new Date(l.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sweeps() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/sweepstakes/cycle").then((r)=>setC(r.data)).catch(()=>{}); }, []);
  if (!c) return null;
  const pct = ((c.cycle_size - c.users_until_draw) / c.cycle_size) * 100;
  return (
    <div className="space-y-6" data-testid="sweeps-panel">
      <h1 className="font-display text-3xl">Sweepstakes Cycle</h1>
      <div className="panel p-8">
        <div className="chip gold">CYCLE #{c.current_cycle}</div>
        <div className="mt-4 font-display text-5xl text-[#d4af37]">${c.prize_amount.toLocaleString()} {c.prize_currency}</div>
        <div className="text-white/60 mt-2">awarded when the vault reaches {c.cycle_size} holders in this cycle.</div>
        <div className="mt-6">
          <div className="flex justify-between text-xs font-mono text-white/50 mb-2">
            <span>{c.total_users} holders</span>
            <span>{c.users_until_draw} until draw</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#00f0ff]" style={{ width: `${pct}%` }} data-testid="sweeps-progress"/>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-white/50">Connecting to the Mint…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen text-white">
      <Toaster theme="dark" richColors position="top-center"/>
      <TopTicker/>
      <div className="flex">
        <Sidebar/>
        <main className="flex-1 p-6 md:p-10 max-w-6xl">
          <Breadcrumbs/>
          <Routes>
            <Route index element={<Overview/>}/>
            <Route path="cards" element={<Cards/>}/>
            <Route path="send" element={<SendMoney/>}/>
            <Route path="topup" element={<TopUp/>}/>
            <Route path="plans" element={<Plans/>}/>
            <Route path="shares" element={<Shares/>}/>
            <Route path="stamps" element={<Stamps/>}/>
            <Route path="atms" element={<Atms/>}/>
            <Route path="chargebacks" element={<Chargebacks/>}/>
            <Route path="iso" element={<ISO/>}/>
            <Route path="merchant" element={<Merchant/>}/>
            <Route path="compliance" element={<Compliance/>}/>
            <Route path="audit" element={<Audit/>}/>
            <Route path="sweeps" element={<Sweeps/>}/>
          </Routes>
        </main>
      </div>
    </div>
  );
}
